import { computed, inject, provide, ref, onMounted, onBeforeUnmount, type Component, type InjectionKey, type Ref } from 'vue'
import { DEFAULT_LABELS, type MaxLabels } from '../core/labels.ts'
import type { InterfaceHost, ResolvedRow, SearchListResult } from '../core/interface-runtime.ts'

export interface MaxRenderContext {
  labels: MaxLabels
  locale: string
  preset: string
  reducedMotion: Ref<boolean>
  /** Hosts may intercept entity references (open a product in POS instead of a URL) */
  openEntity?: (entity: MaxEntity) => void
  /** Entities the host cannot open fall back to their url, or plain text */
  canOpenEntity?: (entity: MaxEntity) => boolean
  /** URL of a product image or Johku file by id; image nodes without one render nothing */
  imageUrl?: (source: string, id: string | number) => string | null
  /** The interface's own icons (spec.icons), drawn instead of a host or built-in icon of the same name */
  icons?: Record<string, { path: string, viewBox?: string, style?: 'stroke' | 'fill' }>
}

export interface MaxEntity { kind: string, id?: string | number, label: string, url?: string }

/** MaxInterface renders openConfirm itself when the host has no confirm */
export type MaxInterfaceHost = Omit<InterfaceHost, 'confirm'> & Partial<Pick<InterfaceHost, 'confirm'>>

/** Provided by MaxInterface only; control nodes render as degraded without it */
export interface MaxInterfaceContext {
  busy: Ref<boolean>
  run (actions: unknown, extra?: Record<string, unknown>): Promise<void>
  setState (name: string, value: unknown): void
  /** The state's current value, also before the next render; controls skip writes that change nothing */
  getState? (name: string): unknown
  setIn (name: string, key: unknown, value: unknown): void
  setSelection (name: string, keys: string[], rows: ResolvedRow[], rowKey: string): void
  canSearchList (name: string): boolean
  searchList (name: string, query: string, offset?: number): Promise<SearchListResult>
}

export function hostOpens (ctx: MaxRenderContext, entity: MaxEntity): boolean {
  if (!ctx.openEntity) return false
  return ctx.canOpenEntity ? ctx.canOpenEntity(entity) : entity.kind !== 'url'
}

const KEY: InjectionKey<MaxRenderContext> = Symbol('max-render-context')

export function provideMaxContext (ctx: MaxRenderContext): void {
  provide(KEY, ctx)
}

export function useMaxContext (): MaxRenderContext {
  return inject(KEY, {
    labels: DEFAULT_LABELS,
    locale: 'en_US',
    preset: 'narrative',
    reducedMotion: ref(false)
  })
}

/** Maps the preset a spec asked for to the one the host renders, e.g. posLook */
export type MaxLook = (preset: string | null) => string

export const MAX_LOOK_KEY: InjectionKey<MaxLook> = Symbol('max-look')

/** An explicit preset prop wins, then the host's look, then the document's own preset. */
export function useMaxPreset (explicit: () => string | null | undefined, requested: () => string | null | undefined) {
  const look = inject(MAX_LOOK_KEY, null)
  return computed(() => explicit() ?? (look ? look(requested() ?? null) : requested()) ?? 'narrative')
}

/**
 * Control node type => host component, so a host draws controls with its own UI (POS draws
 * filterbar as its Bootstrap filter row). The component gets the resolved node as its node
 * prop and talks to the interface through useMaxInterface(), like MaxControl. Wrap its markup
 * in data-max-host so the max.css element resets leave the host's styles alone.
 */
export const MAX_CONTROLS_KEY: InjectionKey<Partial<Record<string, Component>>> = Symbol('max-controls')

export function useMaxHostControl (type: () => string) {
  const controls = inject(MAX_CONTROLS_KEY, {})
  return computed(() => controls[type()] ?? null)
}

const INTERFACE_KEY: InjectionKey<MaxInterfaceContext> = Symbol('max-interface-context')

export function provideMaxInterface (ctx: MaxInterfaceContext): void {
  provide(INTERFACE_KEY, ctx)
}

export function useMaxInterface (): MaxInterfaceContext | null {
  return inject(INTERFACE_KEY, null)
}

export function useReducedMotion (): Ref<boolean> {
  const reduced = ref(false)
  let query: MediaQueryList | null = null
  const update = () => { reduced.value = !!query?.matches }
  onMounted(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    query = window.matchMedia('(prefers-reduced-motion: reduce)')
    update()
    query.addEventListener?.('change', update)
  })
  onBeforeUnmount(() => query?.removeEventListener?.('change', update))
  return reduced
}
